import { useEffect } from 'react';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { inboxCallItemEventBus, type InboxCallItemEvents } from '@frontend/inbox-call-item-event-bus';
import { useQueueStore } from '@frontend/queue';

export interface UseInboxCallItemEventListenerOptions {
  source?:
    | 'callIntel'
    | 'callHistoryContactPanel'
    | 'patientInsightsContactPanel'
    | 'dashboardUnhappyWidget'
    | 'callHistory'
    | 'taskCenter'
    | 'unhappyCallNotification';
}

/**
 * A hook that listens to inbox call item events and handles them with the call insight controller.
 * This decouples the API layer from the UI widget layer by using events for inbox call items.
 *
 * @param options - Configuration options for the event listener
 * @param options.source - The source context for the call insight controller
 */
export const useInboxCallItemEventListener = ({
  source = 'callHistory',
}: UseInboxCallItemEventListenerOptions = {}) => {
  const callInsightController = useCallInsightController({ source });
  const { enqueue } = useQueueStore.getState();

  useEffect(() => {
    const handleCallTagsExternalSync = (data: InboxCallItemEvents['callTagsExternalSync']) => {
      callInsightController.emitEvent('callTagsExternalSync', data);
    };

    const handleOpenCallInsightPanel = (data: InboxCallItemEvents['openCallInsightPanel']) => {
      callInsightController.open({
        channelId: data.channelId,
        includeCallIntelligence: true,
        locationId: data.locationId,
      });
    };

    const handleOpenVoicemailInsightPanel = (data: InboxCallItemEvents['openVoicemailInsightPanel']) => {
      enqueue('voicemail-details', {
        payload: {
          type: 'query-then-open-detached',
          voicemailId: data.voicemailId,
          locationId: data.locationId,
        },
      });
    };

    // Subscribe to events
    inboxCallItemEventBus.on('callTagsExternalSync', handleCallTagsExternalSync);
    inboxCallItemEventBus.on('openCallInsightPanel', handleOpenCallInsightPanel);
    inboxCallItemEventBus.on('openVoicemailInsightPanel', handleOpenVoicemailInsightPanel);

    // Cleanup on unmount
    return () => {
      inboxCallItemEventBus.off('callTagsExternalSync', handleCallTagsExternalSync);
      inboxCallItemEventBus.off('openCallInsightPanel', handleOpenCallInsightPanel);
      inboxCallItemEventBus.off('openVoicemailInsightPanel', handleOpenVoicemailInsightPanel);
    };
  }, [callInsightController]);
};
